// @flow

import * as React from 'react'
import moment from 'moment'
import { Input } from '@/components'
import Strings, { AlignmentTypes, Images, DISPLAY_DATE_FORMAT } from '@/constants'
import Calendar from './calendar'

type CalendarInputProps = {|
  calendarAlignment?: $Values<typeof AlignmentTypes>,
  name: string,
  ariaLabel: string,
  placeholder: string,
  onChange: (data: string) => void,
  value: ?string,
  error?: ?string,
  label?: string,
  // Controls if the user can navigate to months before the current month. default: false
  beforeToday?: boolean,
  // Controls if the user can navigate to months after the current month. default: true
  afterToday?: boolean,
  minDate?: ?moment,
  maxValue?: ?number,
  maxValueUnit?:  ?'year' | 'day' | 'month',
|}

type CalendarInputState = {|
  visible: boolean,
  error: string,
|}

export class CalendarInput extends React.Component<CalendarInputProps, CalendarInputState> {
  today: moment = moment()

  static defaultProps = {
    calendarAlignment: AlignmentTypes.center,
    error: '',
    label: '',
    beforeToday: false,
    afterToday: true,
    minDate: undefined,
    maxValue: null,
    maxValueUnit: null,
  }

  constructor(props: CalendarInputProps) {
    super(props)
    this.state = {
      visible: false,
      error: '',
    }
  }

  onFocus = () => {
    this.setState({ visible: true })
  }

  onBlur = () => {
    this.setState(() => ({ visible: false }), this.validate)
  }

  validate = () => {
    const { value, onChange, maxValueUnit, maxValue, beforeToday, afterToday } = this.props
    const valueMoment = moment(value, DISPLAY_DATE_FORMAT)
    const validBefore = beforeToday ? valueMoment.isBefore(this.today) : true
    const validAfter = afterToday ? valueMoment.isAfter(this.today) : true
    const validMax = maxValue && maxValueUnit ? moment().add(maxValue, maxValueUnit).isAfter(valueMoment) : true
    const validated = valueMoment.isValid() && validBefore && validAfter && validMax
    let error = ''
    if (validated) {
      // format
      const newValue = valueMoment.format(DISPLAY_DATE_FORMAT)
      onChange(newValue)
    } else {
      // setError
      error = Strings.errors.calendarError
    }
    this.setState({ error })
  }

  handleKeyDown = (event: DOMEvent) => {
    // $FlowFixMe
    if (event.keyCode === 13 || event.keyCode === 9) {
      // Enter
      this.onBlur()
    }
  }

  typeDate = (value: string) => {
    const { onChange } = this.props
    onChange(value)
  }

  render() {
    const {
      calendarAlignment, name, placeholder, ariaLabel, onChange, value, error,
      beforeToday, afterToday, maxValue, maxValueUnit, minDate,
      ...rest
    } = this.props
    const { visible, error: stateError } = this.state
    return (
      <Input
        placeholder={placeholder}
        name={name}
        type="text"
        onFocus={this.onFocus}
        dropDown={
          <Calendar
            ariaLabel={`${ariaLabel}-calendar`}
            onChange={onChange}
            requestClose={this.onBlur}
            value={value}
            beforeToday={beforeToday}
            afterToday={afterToday}
            minDate={minDate}
            maxValue={maxValue}
            maxValueUnit={maxValueUnit}
            onKeyDown={this.handleKeyDown}
            alignment={calendarAlignment}
          />
        }
        value={value}
        requestClose={this.onBlur}
        visible={visible}
        ariaLabel={ariaLabel}
        accessory={Images.triangle}
        onChange={this.typeDate}
        error={stateError || error}
        {...rest}
        onKeyDown={this.handleKeyDown}
      />
    )
  }
}

export default CalendarInput
